import { Injectable } from '@angular/core';
import { forkJoin, Observable, of, switchMap, tap } from 'rxjs';
import {
  AccountPointSchema,
  FilterBoundsSchema,
  getAccountPointsResponseSchema,
  GetAccountPointsResponseSchema,
  GetAccountsBaseParamsSchema
} from '../../models/account';
import { HttpClient } from '@angular/common/http';
import { environment } from '../../../environments/environment';
import { decodeZodData } from '../../models/handle-schema';
import { map, take } from 'rxjs/operators';
import { range } from 'lodash';
import { GetAllAccountPointsContext } from './store/get-all-account-points.types';
import { getFunctionUrl } from 'src/app/utils/get-function-url';

const paginationSize = 5000;
const DEFAULT_CONTEXT: GetAllAccountPointsContext = 'default';

@Injectable({
  providedIn: 'root',
})
export class GetAllAccountPointsService {
  // Todo: Use scan in stream
  private readonly boundsCache = new Map<GetAllAccountPointsContext, FilterBoundsSchema[]>();

  constructor(private http: HttpClient) {
  }

  get(
    params: GetAccountsBaseParamsSchema,
    pagination: { page: number, size: number } = { page: 1, size: paginationSize },
    context: GetAllAccountPointsContext = DEFAULT_CONTEXT,
  ): Observable<GetAccountPointsResponseSchema> {
    const boundsCache = this.boundsCache.get(context) ?? [];
    return this.http.post<unknown>(`${ getFunctionUrl(environment.urls.functions.v2, 'accountsapiv2') }/all-points`, {
      ...params,
      filters: {
        ...params.filters,
        geo: {
          ...params.filters?.geo,
          ...(boundsCache.length > 0 ? { exceptBounds: boundsCache } : {}),
        },
      },
      ...pagination,
    }).pipe(
      decodeZodData(getAccountPointsResponseSchema),
    );
  }

  load(
    params: GetAccountsBaseParamsSchema,
    refresh = false,
    context: GetAllAccountPointsContext = DEFAULT_CONTEXT,
  ): Observable<AccountPointSchema[]> {
    if (refresh) this.boundsCache.delete(context);
    return this.get(params, { page: 1, size: paginationSize }, context).pipe(
      switchMap(response => {
        if (response.meta.total > paginationSize) {
          const batchSize = range(2, Math.ceil(response.meta.total / response.meta.size) + 1);
          return forkJoin([ of(response).pipe(take(1)), ...batchSize.map(page =>
            this.get(params, { page, size: response.meta.size }, context)
          ) ]);
        }
        return of([ response ]);
      }),
      map(response => {
        return response.map(r => r.data).flat();
      }),
      tap(() => {
        const bounds = params.filters?.geo?.bounds;
        if (bounds) {
          const cache = this.boundsCache.get(context) ?? [];
          cache.push(bounds);
          this.boundsCache.set(context, cache);
        }
      })
    );
  }
}
